import { AnimatedContentLoader } from '@components'
import { useBusinessContext } from '@context'
import { useQueryParam } from '@hooks'
import { fetchRequisitionStatus, invalidateFirstTransactionFetchNotice } from '@query'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { BankIntegrations } from './BankIntegrations'
import { RequisitionLoader } from './RequisitionLoader'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'

export const IntegrationSettings: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [waitForReference, setWaitForReference] = useQueryParam<string>('reference')

  const { data, error } = useQuery({
    queryKey: [slug, 'wait_for_reference', waitForReference],
    queryFn: () => fetchRequisitionStatus({ slug, reference: waitForReference }),
    enabled: !!waitForReference,
    refetchInterval: 1000
  })

  useEffect(() => {
    if (!data) return
    if (data.status === 'DONE') {
      setWaitForReference(undefined)
      toast.success(t('settings.integrations.bank.integrationCreated'))
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: [slug, 'bank_integrations'] }),
        invalidateFirstTransactionFetchNotice(queryClient, slug)
      ])
    } else if (data.status === 'CANCELLED') {
      setWaitForReference(undefined)
    } else if (data.status === 'FAILED') {
      setWaitForReference(undefined)
      toast.error(t('error.oho'))
    }
  }, [data, queryClient, setWaitForReference, slug, t])

  useEffect(() => {
    if (!error) return
    setWaitForReference(undefined)
    toast.error(t('error.oho'))
  }, [error, setWaitForReference, t])

  return (
    <SettingsSubPageLayout title={t('settings.sections.integrations')}>
      <AnimatePresence mode="wait" initial={false}>
        {waitForReference ? (
          <AnimatedWrapper key="loader">
            <RequisitionLoader />
          </AnimatedWrapper>
        ) : (
          <AnimatedWrapper key="content">
            <AnimatedContentLoader isLoading={!!waitForReference}>
              <BankIntegrations />
            </AnimatedContentLoader>
          </AnimatedWrapper>
        )}
      </AnimatePresence>
    </SettingsSubPageLayout>
  )
}

const AnimatedWrapper = styled(motion.div).attrs({
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 0 }
})``
